import { ISP_STEP_NAME } from '@/views/ISPInstall/pages/step/enums/ISPStepName.enum'
import { useIspInstallStore } from '@/stores'
import { CONSULT_QUERY_KEY, CONSULT_QUERY_ON, isConsultQuery } from '@/views/ISPInstall/utils/consultMode'
import type { RouteLocationNormalized, RouteRecordRaw } from 'vue-router'

/**
 * 手動輸入網址防呆：未完成前一步就直接開 step2/step3 的 URL → 導回應在的步驟。
 * 諮詢模式只認「本次導航明確帶的 ?consult=1」；不看 store.consultMode，因為該旗標會被
 * apptInfo（預約專人來電）設為 true 並持久化殘留，若採信會讓「進過諮詢頁後」的防呆整個失效。
 * 諮詢流程只走 consult-search → appoint-info，不會導航到 search-n-pick/info-form，故此處不放行無妨。
 * 依賴 sessionStorage 於 App 啟動時（main.ts，早於 router）已還原，故重整不會誤踢。
 */
function guardStepPrerequisite(to: RouteLocationNormalized) {
  if (isConsultQuery(to.query)) return true
  const store = useIspInstallStore()

  const hasPlan = !!store.selectedPlan.planDetail
  const hasCommunity =
    !!store.selectedCommunity ||
    !!store.searchNPick_userInputForm.communityName.trim()

  // step2 輸入社區：需 step1 已選方案（select-plan 強制選方案才放行，故合法進入必有方案）
  if (to.name === ISP_STEP_NAME.SEARCH_N_PICK && !hasPlan) {
    return { name: ISP_STEP_NAME.SELECT_PLAN, query: to.query }
  }
  // step3 填寫資訊：需 step2 已決定社區（部分社區狀態不強制選方案，故僅檢查社區）
  if (to.name === ISP_STEP_NAME.INFO_FORM && !hasCommunity) {
    return { name: ISP_STEP_NAME.SEARCH_N_PICK, query: to.query }
  }
  return true
}

/**
 * 進入正常申辦起點（選擇方案）即「關燈」：清掉 apptInfo（預約專人來電）殘留的諮詢旗標。
 * consultMode 會被 apptInfo 設為 true 並持久化，正常流程無處重設 → 進過諮詢頁後回來會卡在
 * 諮詢模式（選方案區被藏、防呆被放行）。select-plan 只屬新申辦流程，故在此無條件關燈。
 */
function resetConsultFlagOnNormalEntry() {
  useIspInstallStore().consultMode = false
  return true
}

/**
 * 諮詢流程防呆：手動輸入網址跳過前置步驟時導回正確位置。
 * 依賴 sessionStorage 於 App 啟動時（main.ts，早於 router）已還原，故流程中重整不會誤導回。
 */
function guardConsultStep(to: RouteLocationNormalized) {
  // 諮詢社區查詢：僅諮詢入口（帶 ?consult=1）可進；否則走正常申裝
  if (to.name === 'ISPInstall_step-consult-search' && !isConsultQuery(to.query)) {
    return { name: ISP_STEP_NAME.SELECT_PLAN }
  }
  // 預約專人來電（填寫資訊）：須已選社區或自行輸入社區名（appoint-info 無合法直接入口）；否則為非法跳步，導回諮詢入口
  if (to.name === 'ISPInstall_appoint-info') {
    const store = useIspInstallStore()
    const hasCommunity =
      !!store.selectedCommunity ||
      !!store.searchNPick_userInputForm.communityName.trim()
    if (!hasCommunity) {
      return { name: 'ISPInstall_step-consult-search', query: { [CONSULT_QUERY_KEY]: CONSULT_QUERY_ON } }
    }
  }
  return true
}

const ISPInstallRoute: RouteRecordRaw = {
  path: '/ISP-install',
  name: 'ISPInstall',
  meta: {},
  component: async () => await import('@/views/ISPInstall/layout.vue'),
  children: [
    {
      path: '',
      name: 'ISPInstall_index',
      redirect: '/ISP-install/step'
    },
    {
      path: 'step-no-banner',
      name: 'ISPInstall_step-nb',
      meta: {
        bannerToggler: false // banner 顯示開關
      },
      component: async () => await import('@/views/ISPInstall/pages/step/redirect.vue')
    },
    {
      path: 'step-default',
      name: 'ISPInstall_default',
      meta: {
        bannerToggler: true // banner 顯示開關
      },
      component: async () => await import('@/views/ISPInstall/pages/step/redirect.vue')
    },
    {
      path: 'step',
      name: 'ISPInstall_step',
      meta: {
        showCartPanel: true
      },
      component: async () => await import('@/views/ISPInstall/pages/step/index.vue'),
      children: [
        {
          path: '',
          name: 'ISPInstall_step-index',
          redirect: '/ISP-install/step/select-plan'
        },
        {
          path: 'select-plan',
          name: ISP_STEP_NAME.SELECT_PLAN,
          meta: {
            stepIndex: 0
          },
          beforeEnter: resetConsultFlagOnNormalEntry,
          component: async () => await import('@/views/ISPInstall/pages/step/pages/selectPlan.vue')
        },
        {
          path: 'search-n-pick',
          name: ISP_STEP_NAME.SEARCH_N_PICK,
          meta: {
            stepIndex: 1
          },
          beforeEnter: guardStepPrerequisite,
          component: async () => await import('@/views/ISPInstall/pages/step/pages/searchNPick.vue')
        },
        {
          // 諮詢專用社區查詢頁（下拉式：縣市→行政區→路名→社區）。新客戶申辦不走此路由
          path: 'consult-search',
          name: 'ISPInstall_step-consult-search',
          meta: {
            stepIndex: 0
          },
          beforeEnter: guardConsultStep,
          component: async () => await import('@/views/ISPInstall/pages/step/pages/consultSearch.vue')
        },
        {
          path: 'info-form',
          name: ISP_STEP_NAME.INFO_FORM,
          meta: {
            stepIndex: 2
          },
          beforeEnter: guardStepPrerequisite,
          component: async () => await import('@/views/ISPInstall/pages/step/pages/infoForm.vue')
        },
        {
          path: 'finish',
          name: ISP_STEP_NAME.FINISH,
          meta: {
            stepIndex: 3
          },
          component: async () => await import('@/views/ISPInstall/pages/step/pages/finish.vue')
        }
      ]
    },
    {
      path: 'finish',
      name: 'ISPInstall_finish',
      meta: {},
      component: async () => await import('@/views/ISPInstall/pages/finish.vue')
    },
    {
      path: 'appoint',
      name: 'ISPInstall_appoint',
      meta: {},
      component: async () => await import('@/views/ISPInstall/pages/appoint.vue')
    },
    {
      path: 'appoint-confirm',
      name: 'ISPInstall_appoint-confirm',
      meta: {},
      component: async () => await import('@/views/ISPInstall/pages/appointConfirm.vue')
    },
    {
      path: 'appoint-info',
      name: 'ISPInstall_appoint-info',
      meta: {
        title: '預約專人來電說明'
      },
      beforeEnter: guardConsultStep,
      component: async () => await import('@/views/ISPInstall/pages/apptInfo.vue')
    }
  ]
}

export default ISPInstallRoute